import { GenericLazyString, gettext } from '@eventbrite/i18n';
import { trackAnalyticsEvent } from '@eventbrite/site-analytics';
import React, { ReactElement } from 'react';
import { LISTING_HEAP_IDS } from '../../../../../constants';
import type { Location } from '../../../../../types';
import { GACodes } from '../../../../../types';
import { Auto, Biking, Bus, Walking } from '../../../../_shared/Icons';

import styles from './HowToGetThereButtons.module.scss';

export const G_MAPS_LINK = 'https://maps.google.com?saddr=Current+Location';

interface ButtonConfig {
    Icon: ReactElement;
    ariaLabel: string | GenericLazyString;
    queryParams: {
        mode?: string;
        dirflg?: string;
    };
    label: string;
    heapId: string;
    text: string | GenericLazyString;
}

const buttonsConfig: ButtonConfig[] = [
    {
        Icon: <Auto className={styles.howToGetThereButtons__icon} />,
        ariaLabel: gettext('Driving directions'),
        queryParams: {
            mode: 'driving',
        },
        label: GACodes.GA_LABEL_DRIVE,
        heapId: LISTING_HEAP_IDS.MAP_DRIVE,
        text: gettext('Driving'),
    },
    {
        Icon: <Bus className={styles.howToGetThereButtons__icon} />,
        ariaLabel: gettext('Transit directions'),
        queryParams: {
            mode: 'transit',
            dirflg: 'r',
        },
        label: GACodes.GA_LABEL_BUS,
        heapId: LISTING_HEAP_IDS.MAP_BUS,
        text: gettext('Public transport'),
    },
    {
        Icon: <Biking className={styles.howToGetThereButtons__icon} />,
        ariaLabel: gettext('Cycling directions'),
        queryParams: {
            mode: 'bicycling',
            dirflg: 'b',
        },
        label: GACodes.GA_LABEL_BIKE,
        heapId: LISTING_HEAP_IDS.MAP_BIKE,
        text: gettext('Biking'),
    },
    {
        Icon: <Walking className={styles.howToGetThereButtons__icon} />,
        ariaLabel: gettext('Walking directions'),
        queryParams: {
            dirflg: 'w',
        },
        label: GACodes.GA_LABEL_WALK,
        heapId: LISTING_HEAP_IDS.MAP_WALK,
        text: gettext('Walking'),
    },
];

const getButtonList = (location: Location): JSX.Element[] =>
    buttonsConfig.map((btnConfig) => {
        const extraParams = new URLSearchParams(
            btnConfig.queryParams,
        ).toString();
        const link = `${G_MAPS_LINK}&daddr=${location.latitude},${location.longitude}&${extraParams}`;
        const label = btnConfig.label;

        const { Icon, text } = btnConfig;

        return (
            <a
                key={btnConfig.text.toString()}
                href={link}
                target="_blank"
                rel="noreferrer"
                className={styles.howToGetThereButtons__link}
                aria-label={btnConfig.ariaLabel.toString()}
                data-heap-id={btnConfig.heapId}
                onClick={() =>
                    trackAnalyticsEvent({
                        category: GACodes.GA_CATEGORY_LISTING,
                        action: GACodes.GA_ACTION_DIRECTIONS,
                        label: label,
                    })
                }
            >
                {Icon} <span>{text}</span>
            </a>
        );
    });

export const HowToGetThereButtons = ({
    location,
    className,
}: {
    location: Location;
    className?: string;
}): JSX.Element => {
    return <div className={className}>{getButtonList(location)}</div>;
};
